import {
	FileText,
	Settings,
	Globe,
	Wrench,
	User,
	Sliders,
	Link2,
	Search,
	ShoppingCart,
	FileCode,
	PenTool,
} from 'lucide-react'
import {
	IconBrandNextjs,
	IconBrandReact,
	IconBrandVue,
	IconBrandLaravel,
	IconBrandDjango,
	IconBrandFramer,
	IconBrandWordpress,
	IconBrandWebflow,
} from '@tabler/icons-react'

export interface NavigationItem {
	id: string
	title: string
	icon?: React.ReactNode
}

export const navigationItems: NavigationItem[] = [
	{ id: 'overview', title: 'Overview', icon: <FileText className="h-4 w-4" /> },
	{
		id: 'general-installation',
		title: 'General Installation',
		icon: <Settings className="h-4 w-4" />,
	},
	{
		id: 'language-configuration',
		title: 'Language Configuration',
		icon: <Globe className="h-4 w-4" />,
	},
	{
		id: 'programmatic-triggers',
		title: 'Programmatic Triggers',
		icon: <Wrench className="h-4 w-4" />,
	},
	{
		id: 'user-identification',
		title: 'User Identification',
		icon: <User className="h-4 w-4" />,
	},
	{
		id: 'widget-control-api',
		title: 'Widget Control API',
		icon: <Sliders className="h-4 w-4" />,
	},
	{
		id: 'nextjs',
		title: 'Next.js',
		icon: <IconBrandNextjs className="h-4 w-4" />,
	},
	{
		id: 'react-vite',
		title: 'React',
		icon: <IconBrandReact className="h-4 w-4" />,
	},
	{ id: 'vuejs', title: 'Vue.js', icon: <IconBrandVue className="h-4 w-4" /> },
	{
		id: 'wordpress',
		title: 'WordPress',
		icon: <IconBrandWordpress className="h-4 w-4" />,
	},
	{
		id: 'laravel',
		title: 'Laravel',
		icon: <IconBrandLaravel className="h-4 w-4" />,
	},
	{
		id: 'django',
		title: 'Django',
		icon: <IconBrandDjango className="h-4 w-4" />,
	},
	{ id: 'webflow', title: 'Webflow', icon: <IconBrandWebflow className="h-4 w-4" /> },
	{
		id: 'framer',
		title: 'Framer',
		icon: <IconBrandFramer className="h-4 w-4" />,
	},
	{
		id: 'shopify',
		title: 'Shopify',
		icon: <ShoppingCart className="h-4 w-4" />,
	},
	{
		id: 'static-html',
		title: 'Static HTML',
		icon: <FileCode className="h-4 w-4" />,
	},
	{
		id: 'webhooks',
		title: 'Webhooks',
		icon: <Link2 className="h-4 w-4" />,
	},
	{
		id: 'troubleshooting',
		title: 'Troubleshooting',
		icon: <Search className="h-4 w-4" />,
	},
]

export const placeholderWidgetId = 'YOUR_WIDGET_ID'

export const embedCode = `<script
    defer
    src="https://cdn.feedbask.com/widget.js"
    data-client-key="${placeholderWidgetId}"
    data-language="en"
    id="feedbask-widget-script"
></script>`

export const aiPrompt = `Please help me implement Feedbask user identification in my codebase. I need to:

1. **Call identify() after user login** - When a user successfully logs in, identify them to Feedbask
2. **Call identify() on page load** - If a user is already logged in when they visit the page
3. **Call unidentify() on logout** - Clear user identification when they log out

Here are the requirements:
- Use window.feedbask.identify({ userId, name, email, ...otherProps }) to identify users
- Use window.feedbask.unidentify() to clear identification on logout
- The userId should be a stable, unique ID from my database (not email or name)
- Name and email are optional but should be included if available
- Add proper error handling with try/catch blocks
- Ensure the widget has loaded before calling these functions

My current authentication setup:
- [Describe your login/logout system here - e.g., "I use NextAuth.js", "I have custom login functions", "I use Firebase Auth", etc.]
- [Describe how you detect if a user is logged in - e.g., "localStorage.getItem('user')", "useUser() hook", "cookies", etc.]
- [Describe your user object structure - e.g., "user has id, full_name, email properties"]

Please provide the specific code I need to add to my project, including:
1. Where to place the identify() call in my login flow
2. How to check for existing logged-in users on page load
3. Where to add the unidentify() call in my logout process
4. Error handling and widget readiness checks

Make sure the code is compatible with my tech stack and follows best practices.`